import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useInjuryEndpoints } from '../../hooks/useInjuryEndpoints';
import { IActiveInjurySummary } from '../../models/injury';
import { InjuryBadge } from '../../shared/InjuryBadge/InjuryBadge';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import itLocale from '@fullcalendar/core/locales/it';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, Tooltip, Portal, AvatarGroup } from '@chakra-ui/react';
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  MapPinLineIcon,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';
import cx from 'classnames';
import { useEndpoints } from '../../hooks/useEndpoints';
import { IEvent } from '../../models/event';
import { EVENT_TYPES } from '../../enums/event';
import { EndpointWithPagination } from '../../typings/Pagination';
import { useDebounce } from '../../hooks/useDebounce';
import { CustomBadge } from '../../shared/CustomBadge/CustomBadge';
import Typhography from '../../shared/Typhography/Typhography';
import CustomAvatar from '../../shared/CustomAvatar/CustomAvatar';
import { addBaseUrl, getLabelFromOptions } from '../../lib/DurationPicker/utils';
import { formatFullName } from '../../utils/functions';
import { EVENT_TYPES_OPTIONS } from '../../constants/config';
import { customPlugin } from '../../components/CalendarPage/Plugin/CusromFlexListView';
import CalendarEventCard from '../../components/CalendarPage/CalendarEventCard';
import WorkoutDetailModal from '../../components/ProgrammingPage/WorkoutDetailModal/WorkoutDetailModal';
import styles from './TeamCalendarTab.module.scss';
import CustomButton from '../../shared/CustomButton/CustomButton';
import AddEventModal from '../../components/CalendarPage/AddEventModal/AddEventModal';
import {
  createEventsFromForm,
  isEventFormInvalid,
} from '../../components/CalendarPage/AddEventModal/eventForm.logic';
import { ITeam } from '../../models/Team';

interface TeamCalendarTabProps {
  teamId: string;
  team: ITeam<any>;
}

const TeamCalendarTab: FC<TeamCalendarTabProps> = ({ teamId, team }) => {
  const calendarRef = useRef<FullCalendar>(null);
  const { getEvents, postEvent } = useEndpoints();
  const [isEventModalOpen, setIsEventModalOpen] = useState<string | undefined>();
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const injuryEndpoints = useInjuryEndpoints();

  const { data: teamInjuries } = useQuery<IActiveInjurySummary[]>({
    queryKey: ['team-injuries', teamId],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getTeamInjuries(teamId);
      return data;
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5,
  });

  const injuredMap = useMemo(() => {
    const map: Record<string, IActiveInjurySummary> = {};
    (teamInjuries ?? []).forEach((inj) => { map[inj.athleteId] = inj; });
    return map;
  }, [teamInjuries]);
  const [events, setEvents] = useState<EndpointWithPagination<IEvent>>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [viewWorkoutId, setViewWorkoutId] = useState<string | null>(null);
  const rangeDate = useRef<{ startDate: Date; endDate: Date }>();
  const { callbackDebounce } = useDebounce();

  const isRecurringEvent = (event: IEvent) =>
    !!(event as any).recurringEventId || !!(event as any).recurrence?.rrule;

  const fetch = async (start: Date, end: Date) => {
    try {
      setIsLoading(true);
      const { data } = await getEvents({
        start: { from: start, to: end },
        teams: [teamId],
        populate:
          'teams.image,teams.name,teams.level,teams:spaceId.type,users.firstName,users.lastName,users.email,users:image.image,testId',
      });
      setEvents(data);
      rangeDate.current = { startDate: start, endDate: end };
    } catch {
      // silently handled
    } finally {
      setIsLoading(false);
    }
  };

  const updateEvents = async (start: Date, end: Date) =>
    callbackDebounce(
      async () => {
        if (!rangeDate.current) {
          fetch(start, end);
          return;
        }
        // Se la range è già coperta non serve rifetchare
        const rangeStart = dayjs(rangeDate.current.startDate).startOf('day');
        const rangeEnd = dayjs(rangeDate.current.endDate).startOf('day');
        const newStart = dayjs(start).startOf('day');
        const newEnd = dayjs(end).startOf('day');
        if (!newStart.isBefore(rangeStart) && !newEnd.isAfter(rangeEnd)) {
          return;
        }
        fetch(start, end);
      },
      'team-cal-fetch-' + teamId,
      80,
    );

  const currentEvents = useMemo(() => {
    if (!events?.docs) return [];
    return events.docs.filter((event) =>
      dayjs(event.date || event.startDate).isSame(selectedDate, 'day'),
    );
  }, [events?.docs, selectedDate]);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      setTitle(calendarApi.view.title);
    }
  }, []);

  // Ricarica quando teamId cambia
  useEffect(() => {
    rangeDate.current = undefined;
    if (calendarRef.current) {
      // getCurrentData() è un'API interna di FullCalendar non esposta nei tipi pubblici
      const api = calendarRef.current.getApi() as unknown as {
        getCurrentData: () => { dateProfile: { activeRange: { start: Date; end: Date } } };
      };
      const { start, end } = api.getCurrentData().dateProfile.activeRange;
      fetch(start, end);
    }
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.root}>
      <div className="d-flex gap-4 w-100" style={{ minHeight: 500 }}>
        {isLoading && (
          <Box className="common-loader" data-state="open" _open={{ animation: 'fade-in 100ms ease-out' }}>
            <LeopartLoader />
          </Box>
        )}

        {/* Left panel: evento del giorno selezionato */}
        <div className={styles.leftPanel}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div>
              <Typhography size="bold-16">
                {dayjs(selectedDate).format('D MMMM YYYY')}
              </Typhography>
              <Typhography size="regular-14" className="color-text-2">
                {dayjs(selectedDate).format('dddd')}
              </Typhography>
            </div>
            <CustomButton
              color="text"
              icon="plus"
              iconPosition="left"
              onClick={() => setIsEventModalOpen(selectedDate.toISOString())}
            >
              Aggiungi evento
            </CustomButton>
          </div>

          <div className="d-flex flex-column w-100 mt-2 gap-3 overflow-auto" style={{ flex: 1 }}>
            {currentEvents.length === 0 ? (
              <div className={styles.emptyDay}>
                <Typhography size="regular-12" className="color-text-2">
                  Nessun evento in questo giorno
                </Typhography>
              </div>
            ) : (
              currentEvents.map((event) => {
                const startDate = dayjs(event.date || event.startDate);
                const endDate = dayjs(event.endDate);
                const hasWorkoutId = !!(event as any).workoutAssignmentId;
                return (
                  <CalendarEventCard
                    key={event._id}
                    title={(event as any)?.name || (event as any)?.testId?.name || (event as any)?.testType || ''}
                    eventType={event.type}
                    workoutSubType={hasWorkoutId ? 'workout-sheet' : undefined}
                    startTime={event.startDate ? startDate.format('HH:mm') : undefined}
                    endTime={event.endDate ? endDate.format('HH:mm') : undefined}
                    location={event.location}
                    extraBadges={
                      <>
                        {isRecurringEvent(event) && (
                          <div className={styles.recurringBadge}>
                            <ArrowsClockwiseIcon size={12} weight="bold" />
                            <Typhography size="regular-12">Ricorrente</Typhography>
                          </div>
                        )}
                        {(event as any).completed && (
                          <div className={styles.completedBadge}>
                            <CheckCircleIcon size={12} weight="fill" />
                            <Typhography size="regular-12">Completato</Typhography>
                          </div>
                        )}
                        {hasWorkoutId && (
                          <div
                            className={styles.linkedBadge}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewWorkoutId((event as any).workoutId);
                            }}
                          >
                            <ClipboardTextIcon size={12} weight="bold" />
                            <Typhography size="regular-12">Scheda</Typhography>
                          </div>
                        )}
                      </>
                    }
                  >
                    <div className="d-flex justify-content-end mt-1">
                      <AvatarGroup gap="0" spaceX="-3" size="2xs">
                        {event.users.slice(0, 3).map((user) => (
                          <Tooltip.Root key={user._id} openDelay={100} closeDelay={0}>
                            <Tooltip.Trigger asChild>
                              <span>
                                <CustomAvatar
                                  name={`${user.firstName[0]}${user.lastName[0]}`}
                                  src={addBaseUrl(1, user.image?.image)}
                                />
                              </span>
                            </Tooltip.Trigger>
                            <Portal>
                              <Tooltip.Positioner>
                                <Tooltip.Content>
                                  <Tooltip.Arrow>
                                    <Tooltip.ArrowTip />
                                  </Tooltip.Arrow>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {formatFullName(user.firstName, user.lastName)}
                                    {injuredMap[user._id] && (
                                      <InjuryBadge size="sm" injury={injuredMap[user._id]} />
                                    )}
                                  </div>
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Portal>
                          </Tooltip.Root>
                        ))}
                        {event.users.slice(3).length > 0 && (
                          <CustomAvatar
                            theme="secondary"
                            name={`+${event.users.slice(3).length}`}
                          />
                        )}
                      </AvatarGroup>
                    </div>
                  </CalendarEventCard>
                );
              })
            )}
          </div>
        </div>

        {/* Right panel: calendario FullCalendar */}
        <div className={styles.rightPanel}>
          <div className={styles.calendarToolbar}>
            <div
              className={styles.calendarNavBtn}
              onClick={() => calendarRef.current?.getApi().prev()}
            >
              <ArrowLeftIcon size={20} />
            </div>
            <Typhography size="bold-14">{title}</Typhography>
            <div
              className={styles.calendarNavBtn}
              onClick={() => calendarRef.current?.getApi().next()}
            >
              <ArrowRightIcon size={20} />
            </div>
          </div>

          <div className="w-100 flex-1 overflow-auto">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, listPlugin, customPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              height="auto"
              headerToolbar={false}
              weekends={true}
              locale={itLocale}
              dateClick={({ date }) => setSelectedDate(date)}
              dayCellClassNames={(arg) =>
                dayjs(arg.date).format('YYYY-MM-DD') === dayjs(selectedDate).format('YYYY-MM-DD')
                  ? 'selected-date'
                  : ''
              }
              eventClick={({ event }) => {
                if (event.start) setSelectedDate(event.start);
              }}
              datesSet={(arg) => {
                const api = calendarRef.current?.getApi();
                updateEvents(arg.start, arg.end);
                api && setTitle(api.view.title);
              }}
              eventClassNames={(arg) => {
                const classes: string[] = [];
                if (!!arg.event.extendedProps.workoutId) {
                  classes.push('event-workout-plan');
                } else if (arg.event.extendedProps.type === EVENT_TYPES.WORKOUT) {
                  classes.push('event-workout');
                } else if (
                  arg.event.extendedProps.type === EVENT_TYPES.TEST ||
                  arg.event.extendedProps.type === EVENT_TYPES.SYSTEM_TEST_EVENT
                ) {
                  classes.push('event-test');
                } else if (arg.event.extendedProps.type === EVENT_TYPES.MATCH) {
                  classes.push('event-match');
                } else {
                  classes.push('event-workout');
                }
                if (arg.event.extendedProps.completed) {
                  classes.push('event-completed');
                }
                return classes;
              }}
              events={events?.docs.map((item) => ({
                title:
                  (item as any)?.name ||
                  (item as any)?.testId?.name ||
                  (item as any)?.testType,
                date: dayjs(item.date || item.startDate).format('YYYY-MM-DD'),
                extendedProps: {
                  id: item._id,
                  type: item.type,
                  completed: (item as any).completed,
                  workoutId: (item as any).workoutId,
                },
              }))}
            />
          </div>
        </div>
      </div>
      <WorkoutDetailModal
        workoutId={viewWorkoutId}
        onClose={() => setViewWorkoutId(null)}
      />
      <AddEventModal
        isOpen={isEventModalOpen}
        isLoading={isConfirmLoading}
        presetTeams={[team]}
        isInvalidForm={isEventFormInvalid}
        onClose={() => setIsEventModalOpen(undefined)}
        onConfirm={async (form) => {
          setIsConfirmLoading(true);
          try {
            await createEventsFromForm(form, postEvent);
            setIsEventModalOpen(undefined);
            if (rangeDate.current) {
              await fetch(rangeDate.current.startDate, rangeDate.current.endDate);
            }
          } catch (error) {
            // rilancia: AddEventModal mappa i 400 di validazione sui campi RHF
            throw error;
          } finally {
            setIsConfirmLoading(false);
          }
        }}
      />
    </div>
  );
};

export default TeamCalendarTab;
